import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

interface UserMenuProps {
  initials?: string;
}

const deriveInitials = (name?: string) => {
  if (!name) return "U";

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
};

const UserMenu: React.FC<UserMenuProps> = ({ initials }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isOwner = user?.role === "OWNER";

  const displayInitials = useMemo(
    () => initials ?? deriveInitials(user?.name),
    [initials, user?.name]
  );

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/login", { replace: true });
  };

  const handleOpenSaasPlans = () => {
    setOpen(false);
    navigate("/owner/saas-plans");
  };

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        type="button"
        className="avatar-sm user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open user menu"
        onClick={() => setOpen((prev) => !prev)}
      >
        {displayInitials}
      </button>

      {open ? (
        <div className="user-menu-popover" role="menu">
          {user?.name ? <div className="user-menu-name">{user.name}</div> : null}
          {isOwner ? (
            <button
              type="button"
              role="menuitem"
              className="user-menu-item"
              onClick={handleOpenSaasPlans}
            >
              <i className="ti ti-package" />
              SAAS Plans
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="user-menu-item"
            onClick={handleLogout}
          >
            <i className="ti ti-logout-2" />
            Logout
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default UserMenu;
